import { type ReactNode } from 'react';
import { X } from 'lucide-react';

/** Overlay: full-bleed on mobile, centered card on sm+. */
export const modalOverlayClass = (zIndexClass = 'z-50') =>
  `fixed inset-0 ${zIndexClass} flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4`;

/** Panel: full viewport height on mobile with flex column for sticky header/footer. */
export const modalPanelClass = (maxWidthClass = 'max-w-xl') =>
  `bg-white dark:bg-gray-800 shadow-xl w-full ${maxWidthClass} flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl`;

export function ModalOverlay({
  children,
  onClose,
  closeOnBackdropClick = false,
  zIndexClass = 'z-50',
}: {
  children: ReactNode;
  onClose?: () => void;
  /** When true, clicking the dimmed backdrop calls `onClose`. Default false for form modals. */
  closeOnBackdropClick?: boolean;
  zIndexClass?: string;
}) {
  return (
    <div
      className={modalOverlayClass(zIndexClass)}
      onClick={closeOnBackdropClick ? onClose : undefined}
      role="presentation"
    >
      {children}
    </div>
  );
}

export function ModalPanel({
  children,
  maxWidthClass = 'max-w-xl',
  titleId,
  className = '',
}: {
  children: ReactNode;
  maxWidthClass?: string;
  titleId?: string;
  className?: string;
}) {
  return (
    <div
      className={`${modalPanelClass(maxWidthClass)} ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      {...(titleId ? { 'aria-labelledby': titleId } : {})}
    >
      {children}
    </div>
  );
}

export function ModalHeader({
  title,
  onClose,
  titleId,
  description,
  icon,
  children,
}: {
  title?: string;
  onClose?: () => void;
  titleId?: string;
  description?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
}) {
  if (children) {
    return (
      <div className="shrink-0 border-b border-gray-200 dark:border-gray-700">{children}</div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
      <div className="flex items-start gap-2 min-w-0">
        {icon ? <span className="mt-0.5 shrink-0">{icon}</span> : null}
        <div className="min-w-0">
          {title ? (
            <h2
              id={titleId}
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {title}
            </h2>
          ) : null}
          {description ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</div>
          ) : null}
        </div>
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="p-1 -m-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      ) : null}
    </div>
  );
}

export function ModalBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`overflow-y-auto flex-1 min-h-0 px-6 py-4 ${className}`.trim()}>
      {children}
    </div>
  );
}

export function ModalFooter({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4 ${className}`.trim()}
    >
      {children}
    </div>
  );
}
